import { useCallback } from 'react';
import {
  useMutation,
  useQueryClient,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { isStaleWrite, type EntityTag } from '@signalvote/shared';
import { rowLedger, type RowTurn } from './versionStore';

/**
 * A row as a read returned it. Its token is a `RowVersion` for the entities on the server's
 * version doors and a plain number for the int-token ones.
 */
export interface VersionedRow<V extends number = number> {
  readonly id: string;
  readonly concurrencyVersion: V;
}

/**
 * A write to one row. `send` must throw an error carrying the server's `code` and `currentVersion` on a 409.
 * It returns the written row, or nothing when the write leaves no row (a delete).
 * `row` is the row as this client last knew it when the write ran, so a body that re-sends fields
 * the edit leaves alone must take them from `row`: a queued write sent after another write to the
 * same row would otherwise send the earlier write's fields back as they were when it was clicked.
 */
export interface RowEndpoint<
  E extends EntityTag,
  V extends number,
  B,
  R extends { concurrencyVersion: V } | void,
  T extends VersionedRow<V> = VersionedRow<V>,
> {
  readonly entity: E;
  send(id: string, concurrencyVersion: V, body: B, row: T): Promise<R>;
}

export type RowWriteInput<V extends number, B, T extends VersionedRow<V> = VersionedRow<V>> = { row: T } & B;

/** Another writer changed the row first. `currentVersion` is null when the row is gone. */
export interface StaleWrite<V extends number = number> {
  kind: 'stale';
  entity: EntityTag;
  id: string;
  currentVersion: V | null;
}

const staleWrites = new WeakMap<object, StaleWrite>();

/** Each started write's place in its row's queue, keyed by the variables TanStack hands back to `mutationFn`. */
const places = new WeakMap<object, RowTurn>();

/** The stale failure a row write threw, or null for any other error. */
export function staleWriteOf<V extends number = number>(error: unknown): StaleWrite<V> | null {
  if (typeof error !== 'object' || error === null) return null;
  return (staleWrites.get(error) as StaleWrite<V> | undefined) ?? null;
}

function currentVersionOf(error: object): number | null {
  const version = (error as { currentVersion?: unknown }).currentVersion;
  return typeof version === 'number' ? version : null;
}

export type RowWriteOptions<V extends number, B, R, C, T extends VersionedRow<V> = VersionedRow<V>> = Omit<
  UseMutationOptions<R, Error, RowWriteInput<V, B, T>, C>,
  'mutationFn'
>;

/**
 * Write one row. The token sent is the freshest one known when the write runs, not when it was
 * asked for: the higher of the row's own and the version store's, which every earlier write to
 * the row that succeeded has fed. Writes to one row run one at a time, in the order they were
 * asked for: a write takes its place when `mutate` is called, before its `onMutate` runs, and gives
 * it up when it settles. Writes to different rows run at once.
 * The thrown error is the endpoint's own; {@link staleWriteOf} and `stale` name a stale one.
 */
export function useRowWrite<
  E extends EntityTag,
  V extends number,
  B,
  R extends { concurrencyVersion: V } | void,
  C = unknown,
  T extends VersionedRow<V> = VersionedRow<V>,
>(
  endpoint: RowEndpoint<E, V, B, R, T>,
  options?: RowWriteOptions<V, B, R, C, T>,
): UseMutationResult<R, Error, RowWriteInput<V, B, T>, C> & { stale: StaleWrite<V> | null } {
  const ledger = rowLedger(useQueryClient());
  const { entity } = endpoint;
  const mutation = useMutation<R, Error, RowWriteInput<V, B, T>, C>({
    ...options,
    mutationFn: async (input) => {
      const place = places.get(input);
      if (!place) throw new Error('A row write starts through its own mutate or mutateAsync.');
      await place.turn;
      const { row, ...body } = input;
      ledger.observe(entity, row.id, row.concurrencyVersion);
      const token = ledger.get(entity, row.id) as V;
      try {
        const written = await endpoint.send(row.id, token, body as B, ledger.freshest(entity, row.id, row));
        if (written) {
          ledger.observe(entity, row.id, written.concurrencyVersion);
          ledger.wrote(entity, row.id, written);
        }
        return written;
      } catch (error) {
        if (typeof error === 'object' && error !== null && isStaleWrite(error)) {
          // The server's current token never enters the store: the caller has not seen the
          // change it stands for, so sending it would overwrite that change unseen.
          staleWrites.set(error, { kind: 'stale', entity, id: row.id, currentVersion: currentVersionOf(error) });
        }
        throw error;
      }
    },
  });
  const { mutateAsync } = mutation;
  const queued = useCallback(
    (input: RowWriteInput<V, B, T>, mutateOptions?: MutateOptions<R, Error, RowWriteInput<V, B, T>, C>) => {
      const place = ledger.reserve(entity, input.row.id);
      const variables = { ...input };
      places.set(variables, place);
      const run = mutateAsync(variables, mutateOptions);
      void run.then(place.release, place.release);
      return run;
    },
    [ledger, entity, mutateAsync],
  );
  const mutate = useCallback(
    (input: RowWriteInput<V, B, T>, mutateOptions?: MutateOptions<R, Error, RowWriteInput<V, B, T>, C>) => {
      queued(input, mutateOptions).catch(() => undefined);
    },
    [queued],
  );
  return Object.assign(mutation, { mutate, mutateAsync: queued, stale: staleWriteOf<V>(mutation.error) });
}
